import { Component, OnDestroy, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, signal } from '@angular/core';
import {NgbActiveModal, NgbModal} from "@ng-bootstrap/ng-bootstrap";
import {ReadDeviceMultiDriverComponent} from "../read-device-multi-driver/read-device-multi-driver.component";
import {AppStateService} from "../core-module/services/app-state.service";
import {Router} from "@angular/router";
import {Subscription} from "rxjs";
import {HttpClient} from "@angular/common/http";
import {environment} from "../../environments/environment";
import {DataLayerService} from "../core-module/services/data-layer.service";
import { AvailableFeaturesStateService } from "../core-module/services/state/available-features-state.service";
import { DeviceConfigStateService } from "../core-module/services/state/device-config-state.service";
import { FeatureUtilsService } from "../core-module/services/state/feature-utils.service";
import { MtpExtrasStateService } from "../core-module/services/state/mtp-extras-state.service";
import {
  DEVICE_SCHEMAS_ENDPOINT,
  DEVICE_SOFTWARE_ENDPOINT,
  MAX_DEVICE_COUNT,
  MIN_DEVICE_COUNT,
  SEARCH_DEVICES_ENDPOINT,
} from "../shared-module/constants/configure-device-modal-input.constants";
import {
  MULTI_DRIVER_MODAL_BACKDROP_CLASS,
  MULTI_DRIVER_MODAL_WINDOW_CLASS,
} from "../shared-module/constants/read-device-multi-driver.constants";

interface DeviceSearchResponse {
  boxQuantity?: number;
}

interface SoftwareItem {
  deviceType?: {
    nameAndVersion?: string;
  };
}

interface DeviceSoftwareResponse {
  items?: SoftwareItem[];
}

interface DeviceSchemaResponse {
  metadata?: {
    schemas?: string[];
  };
}

interface NumericKeyEvent {
  which?: number;
  keyCode?: number;
}

@Component({
  standalone: false,
  selector: "app-configure-device-modal-input",
  templateUrl: "./configure-device-modal-input.component.html",
  styleUrls: ["./configure-device-modal-input.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConfigureDeviceModalInputComponent implements OnInit, OnDestroy {
  boxSize: number | null = null;
  pathVal: string;
  url = environment.multiOneAPIUrl;
  deviceInfo: DeviceSearchResponse[] = [];
  loading = signal(false);
  allFeatures: string[] = [];
  listOfAvailableFeatures: string[] = [];
  apiBoxSize: number = null;
  userBoxSize: number = null;
  wrongInputValue = signal(false);
  device12nc: string;
  private deviceDataLoaded = false;
  ngModelval: Record<string, number | null> = {};
  private subscriptions = new Subscription();

  constructor(
    
    public activemodal: NgbActiveModal,
    private modalService: NgbModal,
    public route: Router,
    private _dataLayerService: DataLayerService,
    private http: HttpClient,
    private appState: AppStateService,
    private utils: FeatureUtilsService,
    private deviceConfig: DeviceConfigStateService,
    private mtpExtras: MtpExtrasStateService,
    private readonly availFeat: AvailableFeaturesStateService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.pathVal = this.utils.getImageUrl(window.location.hostname);
    this.ngModelval['boxSize'] = this.boxSize;

    if (!this.deviceDataLoaded) {
      const device12ncSub = this.deviceConfig.getDevice12nc().subscribe(device12nc => {
        this.device12nc = device12nc;
        this.loadDeviceData(this.device12nc);
        this.deviceDataLoaded = true;
      });
      this.subscriptions.add(device12ncSub);
    }

    const userInputSub = this.deviceConfig.getUserInputBoxValue.subscribe(userValue => {
      if (userValue != null) {
        this.userBoxSize = userValue;
        this.boxSize = userValue;
        this.ngModelval["boxSize"] = this.boxSize;
        this.mtpExtras.sendMultiBoxSizeUser(this.boxSize);
        this.cdr.markForCheck();
      }
    });
    this.subscriptions.add(userInputSub);
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }


  loadDeviceData(device12nc: string) {
    this.loading.set(true);
    this.http.get<any[]>(`${this.url}${SEARCH_DEVICES_ENDPOINT}?device12nc=${device12nc}&showArchive=true`)
      .subscribe({
        next: deviceInfo => {
          this.deviceInfo = deviceInfo;
          const boxquantityvalue = deviceInfo[0]?.boxQuantity ?? 0;
          this.apiBoxSize = boxquantityvalue;
          this.deviceConfig.setBoxQuantityValue(boxquantityvalue);

          // Only set boxSize if user hasn't provided input
          if (this.userBoxSize == null) {
            this.boxSize = boxquantityvalue === 0 ? 1 : boxquantityvalue;
            this.ngModelval['boxSize'] = this.boxSize;
            this.mtpExtras.sendMultiBoxSizeUser(this.boxSize);
          }
          this.getSoftwareAndFeatures(device12nc);
          this.loading.set(false);
          this.cdr.markForCheck();
        },
        error: () => {
          this.loading.set(false);
        }
      });
  }


  getSoftwareAndFeatures(device12nc: string) {
    this.http.get<DeviceSoftwareResponse>(`${this.url}${DEVICE_SOFTWARE_ENDPOINT}/${device12nc}/software`)
      .subscribe(softwareData => {
        const nameAndVersion = softwareData?.items?.[0]?.deviceType?.nameAndVersion;
        const deviceVersion = nameAndVersion?.split(":")[2];
        if (!deviceVersion) {
          return;
        }
        this.http.get<DeviceSchemaResponse>(`${this.url}${DEVICE_SCHEMAS_ENDPOINT}?12nc=${device12nc}&DeviceVersion=${deviceVersion}`)
          .subscribe(data => {
            this.allFeatures = data?.metadata?.schemas || [];
            const schemas = this.allFeatures;
            const features: string[] = [];
            schemas.forEach(schema => {
              const parts = schema.split(':');
              if (parts.length > 1) {
                const featureWithCode = parts[1];
                const featureName = featureWithCode.split('-')[0];
                features.push(featureName);
              }
            });
            this.listOfAvailableFeatures = features;
            this.availFeat.listOfAvailableFeatures = features;
          });
      });
  }

  onNext() {
    const configuration = this.appState.getConfigurationData();
    const dataSet = [];
    for (let i = 0; i < configuration["selectedFeaturesInfo"].length; i++) {
      dataSet.push({
        name: configuration["selectedFeaturesInfo"][i].slice(0, -1),
        action: "included",
      });
    }
    this._dataLayerService.setSelectedDeviceInfo({
      event: "saving",
      savingType: "configBox",
      "12NC": configuration.device12nc,
      "devices": this.boxSize,
      features: dataSet,
    });
    this.mtpExtras.sendShowNextSuccessBtnMulti(false);
    this.modalService.open(ReadDeviceMultiDriverComponent, {
      windowClass: MULTI_DRIVER_MODAL_WINDOW_CLASS,
      backdropClass: MULTI_DRIVER_MODAL_BACKDROP_CLASS,
      backdrop: 'static', // Prevents closing on outside click
      keyboard: false
    });
  }

  onCancel() {
    this.subscriptions.unsubscribe();
    this.deviceConfig.setUserInputBoxValue(null);
    this.route.navigate(["/featureLanding"]);
  }


  numberOfDevices(value: number) {
    this.wrongInputValue.set(value < MIN_DEVICE_COUNT || value > MAX_DEVICE_COUNT);
    if (this.wrongInputValue()) {
      return;
    }
    this.deviceConfig.setUserInputBoxValue(value);
    this.boxSize = value;
    this.ngModelval["boxSize"] = this.boxSize;
    this.mtpExtras.sendMultiBoxSizeUser(this.boxSize);
  }

  numberOnly(event: NumericKeyEvent): boolean {
    const charCode = event.which ? event.which : event.keyCode;
    if (charCode == null) {
      return true;
    }
    if (charCode > 31 && (charCode < 48 || charCode > 57)) {
      return false;
    }
    return true;
  }
}
